import { Injectable, inject } from '@angular/core';

import { HisDdDirective } from '../../directive/his-dd.directive';
import { HisBackendService } from '../backend/his-backend.service';
import { HisGlobalDataService } from '../base/his-global-data.service';

import { HisDynamicFormGroupUtils } from '../base/his-dynamic-form-group-utils';
import { Observable } from 'rxjs';
import { DynamicComponent } from '../../../dynamicform/model/dynamic-content';

class HisDdRegistryObject {
    object: any;
    target: string;
    hisDdDirectives: HisDdDirective[];
    retainHiddenValues: boolean;
    subObjectDynamicContentRef: string;
    
    constructor( object?: any, target?: string, retainHiddenValues?: boolean, subObjectDynamicContentRef?: string ) {
        this.object = object;
        this.target = target;
        this.hisDdDirectives = [];
        this.retainHiddenValues = retainHiddenValues;
        this.subObjectDynamicContentRef = subObjectDynamicContentRef;
    }
}

@Injectable({providedIn: 'root'})
export class HisDdDirectiveRegistryService {
    private backendService = inject(HisBackendService);
    private dynamicFormGroupUtils = inject(HisDynamicFormGroupUtils);
    private globalDataService = inject(HisGlobalDataService);

    private hisDdRegistryObject: HisDdRegistryObject = new HisDdRegistryObject();

    private managedContexts: string[];
    
    private cachedPayload: string;
    
    private readonly FORMGROUPVALUE = 'dynamicformGroupValue';
    
    public getHisDdRegistryDdDirectives(): HisDdDirective[] {
        return this.hisDdRegistryObject.hisDdDirectives;
    }

    public setRegisterObject( object: any, target: string, retainHiddenValues?: boolean, subObjectDynamicContentRef?: string ) {
        this.hisDdRegistryObject = new HisDdRegistryObject( object, target, ( retainHiddenValues !== undefined ? retainHiddenValues : true ), subObjectDynamicContentRef );
    }

    public refreshObject( object: any ) {
        if ( this.hisDdRegistryObject.object ) {
            // this was added to reset form group values
            this.globalDataService.setData( this.FORMGROUPVALUE, null );

            this.hisDdRegistryObject.object = object;
            // console.log( '#HisDdDirectiveRegistryService.refreshObject() this.hisDdRegistryObject: ', this.hisDdRegistryObject );
            
            // when loading onject we need to check for context changes with subscribed object
            this.checkForAnyContextChanges();
        }
    }
    
    public refreshObjectAndResetContextPayload( object: any ) {
        this.cachedPayload = null;
        this.refreshObject( object );
    }

    public registerHisDdDirective( hisDdDirective: HisDdDirective ) {
        if ( this.hisDdRegistryObject.object ) {
            if ( !this.hisDdRegistryObject.hisDdDirectives ) {
                this.hisDdRegistryObject.hisDdDirectives = [];
            }
            if ( this.isNotRegistered( hisDdDirective ) ) {
                // console.log( '#HisDdDirectiveRegistryService.registerHisDdDirective() :', hisDdDirective.hisDdKey );
                this.hisDdRegistryObject.hisDdDirectives.push( hisDdDirective );
            }
        }
    }

    /** @deprecated */
    public checkForContextChanges( hisDdKey: string ): void {
        // console.log( '#HisDdDirectiveRegistryService.checkForContextChanges(): ', hisDdKey );
        // check for triggers for perfermonce - we should not call backend on every change
        if ( ( this.hisDdRegistryObject.object && this.hisDdRegistryObject.object._dynamicComponent !== null )
            && this.hisDdRegistryObject.object._dynamicComponent?.conditions?.triggers.indexOf( hisDdKey ) >= 0 ) {
            if(this.hasPayloadChanged()) {
                this.backendService.postData(
                    `api/v1/cs/sys/objectContextManager/manageContext/${this.hisDdRegistryObject.target}`, this.getManageContextPayload() ).
                    subscribe( ( managedContexts: string[] ) => {
                        this.managedContexts = managedContexts;
                        // console.log( '#HisDdDirectiveRegistryService.checkForContextChanges() managedContexts: ', managedContexts );
                        this.hisDdRegistryObject.hisDdDirectives.forEach(
                            ( hisDdDirective: HisDdDirective ) => {
                                // trigger changes for HisDdDirective
                                this.triggerChanges( hisDdDirective, managedContexts );
                            } );
                    } );
            }
        }
    }
    
    private getManagedContextsForObject(): void {
        if ( this.hasPayloadChanged() ) {
            this.backendService.postData(
                `api/v1/cs/sys/objectContextManager/manageContext/${this.hisDdRegistryObject.target}`, this.getManageContextPayload() ).
                subscribe( ( managedContexts: string[] ) => {
                    this.managedContexts = managedContexts;
                    if (managedContexts != null && managedContexts.length>0) {
                    // console.log( '#HisDdDirectiveRegistryService.checkForContextChanges() managedContexts: ', managedContexts );
                        this.hisDdRegistryObject.hisDdDirectives.forEach(
                            ( hisDdDirective: HisDdDirective ) => {
                                // trigger changes for HisDdDirective
                                this.triggerChanges( hisDdDirective, managedContexts );
                            } );
                    }
                } );
        }
    }
    
    public checkForAnyContextChanges(): void {
        if ( ( this.hisDdRegistryObject.object && this.hisDdRegistryObject.object._dynamicComponent !== null )
            && this.hisDdRegistryObject.object._dynamicComponent?.conditions?.triggers ) {
            this.getManagedContextsForObject();
        }
    }

    private triggerChanges( hisDdDirective: HisDdDirective, contexts: string[] ): void {
        if (hisDdDirective) {
            hisDdDirective.hisDdContexts = [...contexts];
            hisDdDirective.findElementRef().hisDdContexts = [...contexts];
            hisDdDirective.notifyDdKeyOrContextsChanged();          
        }
    }

    private getManageContextPayload() {
        this.cachedPayload = JSON.stringify( this.hisDdRegistryObject.object );
        
        if ( this.hisDdRegistryObject.object._dynamicComponent?.dynamicObjects && !!this.hisDdRegistryObject.object._dynamicComponent.dynamicObjects.length ) {
            // create new object without reference, cause setDynamicComponentObject()
            // should not set dynamicObject values on original object
            let copyObject = JSON.parse( this.cachedPayload );
            // update dynamic objects from formGroupValues
            this.dynamicFormGroupUtils.setDynamicComponentObject( copyObject );

            return copyObject;
        }
        return this.hisDdRegistryObject.object;
    }
    
    private hasPayloadChanged() : boolean {
        this.dynamicFormGroupUtils.setDynamicComponentObject( this.hisDdRegistryObject.object );
        if ( this.hisDdRegistryObject.subObjectDynamicContentRef ) {
            this.dynamicFormGroupUtils.setDynamicComponentObject( this.hisDdRegistryObject.object[this.hisDdRegistryObject.subObjectDynamicContentRef] );
        }
        return this.cachedPayload !== JSON.stringify( this.hisDdRegistryObject.object );
    }

    public getManagedContexts(): string [] {
        return this.managedContexts;
    }

    public resetObjectValue( ddKey: string ): void {
        let attributeName = ddKey.split( '.' ).pop();
        if ( this.hisDdRegistryObject && this.hisDdRegistryObject.object && Object.prototype.hasOwnProperty.call( this.hisDdRegistryObject.object, attributeName ) ) {
            if(!this.hisDdRegistryObject.retainHiddenValues) {
                this.hisDdRegistryObject.object[attributeName] = null;
            }
        }
    }

    public clearHisDdRegistryObject(): void {
        this.hisDdRegistryObject = new HisDdRegistryObject();
    }

    public debugHisDdRegistryObject(): void {
        console.log( '#HisDdDirectiveRegistryService.debugHisDdRegistryObject: ', this.hisDdRegistryObject );
    }

    public debugHisDdRegistryDirectives(): void {
        this.hisDdRegistryObject.hisDdDirectives.forEach(
            ( ddDirective: HisDdDirective ) =>
                console.log( '##HisDdDirectiveRegistryService.debugHisDdRegistryDirectives hisDdKey: ', ddDirective.hisDdKey ) );
    }

    private isNotRegistered( hisDdDirective: HisDdDirective ): boolean {
        return this.hisDdRegistryObject.hisDdDirectives.indexOf( hisDdDirective ) === -1;
    }

    public generateInitialDynamicContent(): Observable<DynamicComponent> {
        if(!this.hisDdRegistryObject.target) {
            console.error('No target specified. Did you call \'registerHisDdDirective\' before?');
        }
        return this.backendService.getData( `api/v1/cs/sys/objectContextManager/generateConditions/${this.hisDdRegistryObject.target}`);
    }
}
